// src/components/billing/BillingSettings.tsx
/**
 * Comprehensive account settings page.
 * Includes billing, usage dashboard, environments, and invoices.
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../../i18n";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  useTheme,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import FolderIcon from "@mui/icons-material/Folder";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BugReportIcon from "@mui/icons-material/BugReport";
import ScienceIcon from "@mui/icons-material/Science";
import { useSnackbar } from "notistack";
import {
  useOrganization,
  useSubscriptionTiers,
  useInvoices,
  useBilling,
} from "../../hooks/useBilling";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import UsageDashboard from "./UsageDashboard";
import PlanSelector from "./PlanSelector";
import ContractsLegalTab from "./ContractsLegalTab";
import AdminPricingPanel from "./AdminPricingPanel";
import AdminBetaUsersPanel from "./AdminBetaUsersPanel";
import AdminLegalReviewPanel from "./AdminLegalReviewPanel";
import api from "../../api/api";
import { useSelector } from "react-redux";
import { RootState } from "types";
import {
  athenaDarkPaperSx,
  athenaBackdropSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaHeaderIconBoxSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
  athenaColors,
} from "../layout/AthenaDialogStyles";
import logger from "utils/logger";
import { glass, glassTableSx } from "../../theme/glassTokens";
import VideoGlassContainer from "../dashboards/VideoGlassContainer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// Types for environments
interface Environment {
  id: string;
  name: string;
  owner_organization?: { id: string; name: string } | null;
  allow_export: boolean;
  allow_ai_extraction: boolean;
  require_patient_anonymization: boolean;
  administrators?: { id: string; email: string; username?: string }[];
  patient_count?: number;
}

interface EnvironmentMember {
  id: number;
  user: { id: string; email: string; username?: string; first_name?: string; last_name?: string };
  role: "owner" | "admin" | "editor" | "contributor" | "viewer";
  can_export: boolean;
  can_use_ai: boolean;
  can_view_patient_names: boolean;
  is_active: boolean;
  invited_at: string;
  accepted_at?: string;
  status?: "pending" | "active" | "declined";
}

interface SearchUser {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

interface PlatformUser {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner", description: "Full control, can delete environment" },
  { value: "admin", label: "Admin", description: "Full control except delete" },
  { value: "editor", label: "Editor", description: "Can view, add, and edit" },
  { value: "contributor", label: "Contributor", description: "Can view and add only" },
  { value: "viewer", label: "Viewer", description: "Read-only access" },
];

const BillingSettings: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = currentUser?.is_system_admin === true;
  const [activeTab, setActiveTab] = useState(3);
  const [editingName, setEditingName] = useState(false);
  const [orgName, setOrgName] = useState("");

  // Environment state
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [envLoading, setEnvLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<PlatformUser[]>([]);
  const [createEnvOpen, setCreateEnvOpen] = useState(false);
  const [newEnvName, setNewEnvName] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("viewer");
  const [envMembers, setEnvMembers] = useState<EnvironmentMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [editEnvOpen, setEditEnvOpen] = useState(false);
  const [editingEnv, setEditingEnv] = useState<Environment | null>(null);
  // Members modal
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  // Invite autocomplete
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const [inviteInputValue, setInviteInputValue] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hooks
  const {
    organization,
    loading: orgLoading,
    updateSettings,
    refetch: refetchOrg,
  } = useOrganization();
  const { tiers, loading: tiersLoading } = useSubscriptionTiers();
  const { invoices, loading: invoicesLoading } = useInvoices();
  const {
    createCheckout,
    openCustomerPortal,
    cancelSubscription,
    checkoutLoading,
    portalLoading,
    cancelLoading,
  } = useBilling();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const {
    isBetaUser,
    isSimulating,
    simulatedTierName,
    setSimulatedTier,
    simulatableTiers,
  } = useFeatureGate();

  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;

  // Fetch environments
  useEffect(() => {
    const fetchEnvironments = async () => {
      setEnvLoading(true);
      try {
        const response = await api.get("/environments/");
        setEnvironments(response.data || []);
      } catch (err) {
        logger.error("Error fetching environments:", err);
      } finally {
        setEnvLoading(false);
      }
    };
    fetchEnvironments();
  }, []);

  // Fetch available users for invitation (legacy, kept for backward compat)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/auth/users/");
        setAvailableUsers(response.data || []);
      } catch (err) {
        logger.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  // Handle checkout success/cancel redirect from Stripe
  useEffect(() => {
    const checkoutStatus = searchParams.get("checkout");
    if (checkoutStatus === "success") {
      enqueueSnackbar(t("billing.checkout_success", "Subscription activated successfully!"), {
        variant: "success",
        autoHideDuration: 6000,
      });
      setActiveTab(0);
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
      refetchOrg();
    } else if (checkoutStatus === "cancelled") {
      enqueueSnackbar(t("billing.checkout_cancelled", "Checkout was cancelled."), {
        variant: "info",
        autoHideDuration: 4000,
      });
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, enqueueSnackbar, refetchOrg]);

  // User search for invite autocomplete (debounced)
  const handleSearchUsers = useCallback((query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (query.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/auth/users/search/?q=${encodeURIComponent(query)}`);
        setSearchResults(response.data || []);
      } catch (err) {
        logger.error("Error searching users:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  // Fetch members for a given environment
  const fetchMembers = useCallback(async (envId: string) => {
    setMembersLoading(true);
    try {
      const response = await api.get(`/environments/${envId}/members/`);
      setEnvMembers(response.data || []);
    } catch (err) {
      logger.error("Error fetching members:", err);
      setEnvMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  // Open members modal
  const handleOpenMembers = useCallback((env: Environment) => {
    setSelectedEnv(env);
    setMembersModalOpen(true);
    fetchMembers(env.id);
  }, [fetchMembers]);

  // Update member role
  const handleUpdateMemberRole = useCallback(async (envId: string, memberId: number, newRole: string) => {
    try {
      await api.patch(`/environments/${envId}/members/${memberId}/`, { role: newRole });
      enqueueSnackbar(t("billing.member_role_updated", "Member role updated"), { variant: "success" });
      fetchMembers(envId);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("billing.error_update_member", "Failed to update member"), { variant: "error" });
    }
  }, [enqueueSnackbar, fetchMembers]);

  // Remove member
  const handleRemoveMember = useCallback(async (envId: string, memberId: number) => {
    try {
      await api.delete(`/environments/${envId}/members/${memberId}/remove/`);
      enqueueSnackbar(t("billing.member_removed", "Member removed"), { variant: "success" });
      fetchMembers(envId);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("billing.error_remove_member", "Failed to remove member"), { variant: "error" });
    }
  }, [enqueueSnackbar, fetchMembers]);

  // Handle plan selection
  const handleSelectTier = useCallback(
    async (tierName: string) => {
      try {
        const checkoutUrl = await createCheckout(tierName);
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        }
      } catch (err: any) {
        enqueueSnackbar(err.message || t("billing.error_start_checkout", "Failed to start checkout"), {
          variant: "error",
        });
      }
    },
    [createCheckout, enqueueSnackbar]
  );

  // Handle Stripe portal
  const handleOpenPortal = useCallback(async () => {
    try {
      const portalUrl = await openCustomerPortal();
      if (portalUrl) {
        window.open(portalUrl, "_blank");
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || t("billing.error_open_portal", "Failed to open billing portal"), {
        variant: "error",
      });
    }
  }, [openCustomerPortal, enqueueSnackbar]);

  // Handle subscription cancellation
  const handleConfirmCancel = useCallback(async () => {
    try {
      const result = await cancelSubscription();
      enqueueSnackbar(result.message, {
        variant: "success",
        autoHideDuration: 8000,
      });
      setCancelDialogOpen(false);
      refetchOrg();
    } catch (err: any) {
      enqueueSnackbar(err.message || t("billing.cancel_subscription_failed", "Failed to cancel subscription"), {
        variant: "error",
      });
    }
  }, [cancelSubscription, enqueueSnackbar, refetchOrg]);

  // Handle overage toggle
  const handleOverageToggle = useCallback(
    async (enabled: boolean) => {
      try {
        await updateSettings({ overage_enabled: enabled });
        enqueueSnackbar(
          enabled
            ? t("billing.overage_enabled_msg", "Overage billing enabled. You will be charged for usage beyond your quota.")
            : t("billing.overage_disabled_msg", "Overage billing disabled. Usage will be blocked when quota is reached."),
          { variant: "success" }
        );
      } catch (err: any) {
        enqueueSnackbar(err.message || t("billing.error_update_settings", "Failed to update settings"), {
          variant: "error",
        });
      }
    },
    [updateSettings, enqueueSnackbar]
  );

  // Handle org name update
  const handleSaveName = useCallback(async () => {
    if (!orgName.trim()) return;
    try {
      await updateSettings({ name: orgName.trim() });
      setEditingName(false);
      enqueueSnackbar(t("billing.org_name_updated", "Organization name updated"), { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.message || t("billing.error_update_name", "Failed to update name"), {
        variant: "error",
      });
    }
  }, [orgName, updateSettings, enqueueSnackbar]);

  // Create new environment
  const handleCreateEnvironment = useCallback(async () => {
    if (!newEnvName.trim()) {
      enqueueSnackbar(t("billing.env_name_required", "Environment name is required"), { variant: "warning" });
      return;
    }
    try {
      const response = await api.post("/environments/", { name: newEnvName.trim() });
      setEnvironments((prev) => [response.data, ...prev]);
      setNewEnvName("");
      setCreateEnvOpen(false);
      enqueueSnackbar(t("billing.env_created", "Environment created successfully"), { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("billing.error_create_env", "Failed to create environment"), {
        variant: "error",
      });
    }
  }, [newEnvName, enqueueSnackbar]);

  // Update environment settings
  const handleUpdateEnvironment = useCallback(async () => {
    if (!editingEnv) return;
    try {
      const response = await api.patch(`/environments/${editingEnv.id}/`, {
        name: editingEnv.name,
        allow_export: editingEnv.allow_export,
        allow_ai_extraction: editingEnv.allow_ai_extraction,
        require_patient_anonymization: editingEnv.require_patient_anonymization,
      });
      setEnvironments((prev) =>
        prev.map((e) => (e.id === editingEnv.id ? response.data : e))
      );
      setEditEnvOpen(false);
      setEditingEnv(null);
      enqueueSnackbar(t("billing.env_updated", "Environment updated"), { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("billing.error_update_env", "Failed to update environment"), {
        variant: "error",
      });
    }
  }, [editingEnv, enqueueSnackbar]);

  // Invite user to environment
  const handleInviteUser = useCallback(async () => {
    const email = selectedUser?.email || inviteInputValue.trim();
    if (!selectedEnv || !email) return;
    try {
      await api.post(`/environments/${selectedEnv.id}/invite/`, {
        email,
        role: inviteRole,
      });
      setInviteInputValue("");
      setSelectedUser(null);
      setInviteRole("viewer");
      setSearchResults([]);
      setInviteModalOpen(false);
      enqueueSnackbar(t("billing.invitation_sent", "Invitation sent successfully. The user will receive a notification."), { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("billing.error_send_invitation", "Failed to send invitation"), {
        variant: "error",
      });
    }
  }, [selectedEnv, selectedUser, inviteInputValue, inviteRole, enqueueSnackbar]);

  // Get role chip color
  const getRoleColor = (role: string) => {
    switch (role) {
      case "owner": return "error";
      case "admin": return "warning";
      case "editor": return "primary";
      case "contributor": return "info";
      case "viewer": return "default";
      default: return "default";
    }
  };

  // Loading state
  if (orgLoading) {
    return (
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          p: { md: 0.5, lg: 2,  xl: 3 },
          minHeight: 0,
        }}
      >
        {/* Wrapped in the same chrome as the loaded state: the app-level container is
            transparent for this route (pageSuppliesOwnChrome), so unwrapped skeletons
            would float on the bare page gradient and the card would "appear" on load. */}
        <VideoGlassContainer
          variant="dark"
          glassSx={{ display: "flex", flexDirection: "column", "& > *": { flexShrink: 0 } }}
        >
          <Skeleton variant="rectangular" height={60} sx={{
            mb: 3,
            borderRadius: 2,
            background: glass.panel,
            backdropFilter: "blur(8px)",
          }} />
          <Skeleton variant="rectangular" height={400} sx={{
            borderRadius: 2,
            background: glass.panel,
            backdropFilter: "blur(8px)",
          }} />
        </VideoGlassContainer>
      </Container>
    );
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "trialing":
        return "info";
      case "past_due":
        return "warning";
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        p: { md: 0.5, lg: 2,  xl: 3 },
        minHeight: 0,
      }}
    >
      {/* Same chrome as /environments, WelcomeHeroCard and BulkUploadResults.
          `"& > *": { flexShrink: 0 }` is REQUIRED, not cosmetic: the glass card is a
          flex COLUMN with a max height, so children default to flex-shrink:1 and get
          squeezed until they clip their own content. */}
      <VideoGlassContainer
        variant="dark"
        glassSx={{
          display: "flex",
          flexDirection: "column",
          "& > *": { flexShrink: 0 },
        }}
      >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary }} gutterBottom>
          {t("billing.account_settings", "Account Settings")}
        </Typography>
        <Typography variant="body2" sx={{ color: textSecondary }}>
          {t("billing.account_description", "Manage your environments, subscription, usage, and team.")}
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: glass.panelBorder }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              color: textSecondary,
              "&.Mui-selected": {
                color: textPrimary,
              },
            },
          }}
        >
          {/* Display order: Settings first; explicit value props keep the
              TabPanel index mapping stable regardless of visual order. */}
          <Tab value={3} label={t("billing.tab_settings", "Settings")} />
          <Tab value={0} label={t("billing.tab_usage", "Usage")} />
          <Tab value={1} label={t("billing.tab_plans", "Plans")} />
          <Tab value={2} label={t("billing.tab_invoices", "Invoices")} />
          <Tab value={4} label={t("billing.tab_contracts_legal", "Contracts & Legal")} />
          {isSuperAdmin && (
            <Tab value={5} label={t("billing.tab_admin_pricing", "Pricing")} />
          )}
        </Tabs>
      </Box>

      {/* Usage Tab */}
      <TabPanel value={activeTab} index={0}>
        <UsageDashboard onUpgradeClick={() => setActiveTab(1)} />
      </TabPanel>

      {/* Legacy Environments section - now at /environments page */}
      {/* Keeping the dialogs here for backward compat but hiding the tab */}
      <TabPanel value={activeTab} index={-1}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{t("billing.your_environments", "Your Environments")}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateEnvOpen(true)}
          >
            {t("billing.new_environment", "New Environment")}
          </Button>
        </Box>

        {envLoading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : environments.length === 0 ? (
          <Card sx={{ textAlign: "center", py: 6 }}>
            <CardContent>
              <FolderIcon sx={{ fontSize: 64, color: glass.textAbsent, mb: 2 }} />
              <Typography variant="h6" color={glass.textSecondary} gutterBottom>
                {t("billing.no_environments", "No environments yet")}
              </Typography>
              <Typography variant="body2" color={glass.textSecondary} sx={{ mb: 3 }}>
                {t("billing.no_environments_desc", "Create your first environment to start organizing patient data.")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateEnvOpen(true)}
              >
                {t("billing.create_environment", "Create Environment")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {environments.map((env) => (
              <Grid item xs={12} md={6} lg={4} key={env.id}>
                <Card
                  sx={{
                    height: "100%",
                    position: "relative",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FolderIcon color="primary" />
                        <Typography variant="h6" noWrap sx={{ maxWidth: 180 }}>
                          {env.name || t("billing.unnamed", "Unnamed")}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingEnv({ ...env });
                          setEditEnvOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Feature flags */}
                    <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {env.allow_ai_extraction && (
                        <Chip label={t("billing.ai_enabled_chip", "AI Enabled")} size="small" color="primary" variant="outlined" />
                      )}
                      {env.allow_export && (
                        <Chip label={t("billing.export_chip", "Export")} size="small" color="success" variant="outlined" />
                      )}
                      {env.require_patient_anonymization && (
                        <Chip label={t("billing.anonymized_chip", "Anonymized")} size="small" color="warning" variant="outlined" />
                      )}
                    </Box>

                    {/* Stats */}
                    <Typography variant="body2" color={glass.textSecondary} sx={{ mb: 1 }}>
                      {env.patient_count || 0} {t("billing.patients_count", "patients")}
                    </Typography>

                    {/* Owner Organization */}
                    {env.owner_organization && (
                      <Typography variant="caption" color={glass.textSecondary} display="block" sx={{ mb: 2 }}>
                        {t("billing.billed_to", "Billed to:")} {env.owner_organization.name}
                      </Typography>
                    )}

                    {/* Actions */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => {
                          setSelectedEnv(env);
                          setInviteModalOpen(true);
                        }}
                      >
                        {t("billing.invite", "Invite")}
                      </Button>
                      <Button
                        size="small"
                        startIcon={<GroupIcon />}
                        onClick={() => handleOpenMembers(env)}
                      >
                        {t("billing.members", "Members")}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Create Environment Dialog */}
        <Dialog open={createEnvOpen} onClose={() => setCreateEnvOpen(false)} maxWidth="sm" fullWidth slotProps={{ backdrop: { sx: athenaBackdropSx } }} PaperProps={{ sx: athenaDarkPaperSx }}>
          <DialogTitle sx={athenaDarkTitleSx}>
            <Box sx={athenaHeaderIconBoxSx}><AddIcon sx={{ color: athenaColors.periwinkle, fontSize: 22 }} /></Box>
            <Typography variant="h6" fontWeight={700}>{t("billing.create_new_environment", "Create New Environment")}</Typography>
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx}>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label={t("billing.environment_name", "Environment Name")}
                value={newEnvName}
                onChange={(e) => setNewEnvName(e.target.value)}
                placeholder={t("billing.environment_placeholder", "e.g., Cancer Research Study 2024")}
                helperText={t("billing.environment_helper", "Give your environment a descriptive name")}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={athenaDarkActionsSx}>
            <Button variant="outlined" onClick={() => setCreateEnvOpen(false)} sx={athenaDarkSecondaryButtonSx}>{t("action.cancel", "Cancel")}</Button>
            <Button variant="contained" onClick={handleCreateEnvironment} sx={athenaDarkPrimaryButtonSx}>
              {t("action.create", "Create")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Environment Dialog */}
        <Dialog open={editEnvOpen} onClose={() => setEditEnvOpen(false)} maxWidth="sm" fullWidth slotProps={{ backdrop: { sx: athenaBackdropSx } }} PaperProps={{ sx: athenaDarkPaperSx }}>
          <DialogTitle sx={athenaDarkTitleSx}>
            <Box sx={athenaHeaderIconBoxSx}><EditIcon sx={{ color: athenaColors.periwinkle, fontSize: 22 }} /></Box>
            <Typography variant="h6" fontWeight={700}>{t("billing.edit_environment", "Edit Environment")}</Typography>
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx}>
            {editingEnv && (
              <Box sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label={t("billing.environment_name", "Environment Name")}
                  value={editingEnv.name || ""}
                  onChange={(e) => setEditingEnv({ ...editingEnv, name: e.target.value })}
                  sx={{ mb: 3 }}
                />
                
                <Typography variant="subtitle2" gutterBottom>
                  {t("billing.feature_flags", "Feature Flags")}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingEnv.allow_ai_extraction}
                      onChange={(e) =>
                        setEditingEnv({ ...editingEnv, allow_ai_extraction: e.target.checked })
                      }
                    />
                  }
                  label={t("billing.allow_ai_extraction", "Allow AI Extraction")}
                />
                <Typography variant="caption" display="block" color={glass.textSecondary} sx={{ ml: 6, mb: 2 }}>
                  {t("billing.ai_extraction_desc", "Enable AI-powered data extraction for this environment")}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={editingEnv.allow_export}
                      onChange={(e) =>
                        setEditingEnv({ ...editingEnv, allow_export: e.target.checked })
                      }
                    />
                  }
                  label={t("billing.allow_export", "Allow Data Export")}
                />
                <Typography variant="caption" display="block" color={glass.textSecondary} sx={{ ml: 6, mb: 2 }}>
                  {t("billing.export_desc", "Allow users to export patient data from this environment")}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={editingEnv.require_patient_anonymization}
                      onChange={(e) =>
                        setEditingEnv({ ...editingEnv, require_patient_anonymization: e.target.checked })
                      }
                    />
                  }
                  label={t("billing.require_anonymization", "Require Anonymization")}
                />
                <Typography variant="caption" display="block" color={glass.textSecondary} sx={{ ml: 6 }}>
                  {t("billing.anonymization_desc", "Hide patient names for non-admin users")}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={athenaDarkActionsSx}>
            <Button variant="outlined" onClick={() => setEditEnvOpen(false)} sx={athenaDarkSecondaryButtonSx}>{t("action.cancel", "Cancel")}</Button>
            <Button variant="contained" onClick={handleUpdateEnvironment} sx={athenaDarkPrimaryButtonSx}>
              {t("billing.save_changes", "Save Changes")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite User Dialog */}
        <Dialog
          open={inviteModalOpen}
          onClose={() => {
            setInviteModalOpen(false);
            setSearchResults([]);
            setSelectedUser(null);
            setInviteInputValue("");
          }}
          maxWidth="sm"
          fullWidth
          slotProps={{ backdrop: { sx: athenaBackdropSx } }}
          PaperProps={{ sx: athenaDarkPaperSx }}
        >
          <DialogTitle sx={athenaDarkTitleSx}>
            <Box sx={athenaHeaderIconBoxSx}><PersonAddIcon sx={{ color: athenaColors.periwinkle, fontSize: 22 }} /></Box>
            <Typography variant="h6" fontWeight={700}>{t("billing.invite_to", "Invite to")} {selectedEnv?.name}</Typography>
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx}>
            <Box sx={{ pt: 1 }}>
              <Autocomplete<SearchUser, false, false, true>
                freeSolo
                options={searchResults}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  const name = `${option.first_name || ""} ${option.last_name || ""}`.trim();
                  return name ? `${option.email} (${name})` : option.email;
                }}
                value={selectedUser}
                inputValue={inviteInputValue}
                onInputChange={(_e, newValue, reason) => {
                  setInviteInputValue(newValue);
                  if (reason === "input") {
                    handleSearchUsers(newValue);
                  }
                }}
                onChange={(_e, newValue) => {
                  if (typeof newValue === "string") {
                    setSelectedUser(null);
                    setInviteInputValue(newValue);
                  } else {
                    setSelectedUser(newValue);
                  }
                }}
                loading={searchLoading}
                filterOptions={(x) => x}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: alpha(glass.accent, 0.15), color: glass.accent }}>
                        {(option.first_name?.[0] || option.email[0]).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {`${option.first_name || ""} ${option.last_name || ""}`.trim() || option.username || "User"}
                        </Typography>
                        <Typography variant="caption" color={glass.textSecondary} noWrap display="block">
                          {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t("billing.search_by_email", "Search by email")}
                    placeholder={t("billing.search_email_placeholder", "Start typing an email address...")}
                    helperText={t("billing.search_email_helper", "Type at least 3 characters to search. You can also type a full email for users not yet on the platform.")}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ mb: 3 }}
              />

              <FormControl fullWidth>
                <InputLabel>{t("billing.role_label", "Role")}</InputLabel>
                <Select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  label={t("billing.role_label", "Role")}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Box>
                        <Typography>{t(`billing.role_${role.value}`, role.label)}</Typography>
                        <Typography variant="caption" color={glass.textSecondary}>
                          {t(`billing.role_${role.value}_desc`, role.description)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={athenaDarkActionsSx}>
            <Button variant="outlined" onClick={() => {
              setInviteModalOpen(false);
              setSearchResults([]);
              setSelectedUser(null);
              setInviteInputValue("");
            }} sx={athenaDarkSecondaryButtonSx}>
              {t("action.cancel", "Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleInviteUser}
              disabled={!(selectedUser?.email || inviteInputValue.trim())}
              sx={athenaDarkPrimaryButtonSx}
            >
              {t("billing.send_invitation", "Send Invitation")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Members Dialog */}
        <Dialog
          open={membersModalOpen}
          onClose={() => setMembersModalOpen(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{ backdrop: { sx: athenaBackdropSx } }}
          PaperProps={{ sx: athenaDarkPaperSx }}
        >
          <DialogTitle sx={athenaDarkTitleSx}>
            <Box sx={athenaHeaderIconBoxSx}><GroupIcon sx={{ color: athenaColors.periwinkle, fontSize: 22 }} /></Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>{t("billing.members_of", "Members of")} {selectedEnv?.name}</Typography>
            </Box>
            <Chip
              label={`${envMembers.length} member${envMembers.length !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
              sx={{ color: theme.palette.common.white, borderColor: alpha(athenaColors.periwinkle, 0.4), fontWeight: 600 }}
            />
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx}>
            {membersLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : envMembers.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <GroupIcon sx={{ fontSize: 48, color: glass.textAbsent, mb: 1 }} />
                <Typography color={glass.textSecondary}>
                  {t("billing.no_members", "No members yet. Invite someone to get started.")}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {envMembers.map((member) => {
                  const userName = `${member.user.first_name || ""} ${member.user.last_name || ""}`.trim()
                    || member.user.username || "User";
                  const initials = (member.user.first_name?.[0] || member.user.email[0]).toUpperCase();
                  const isPending = member.status === "pending" || (!member.accepted_at && member.is_active);

                  return (
                    <ListItem
                      key={member.id}
                      divider
                      sx={{ py: 1.5, alignItems: "flex-start" }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: isPending
                              ? alpha(theme.palette.warning.main, 0.15)
                              : alpha(glass.accent, 0.15),
                            color: isPending
                              ? theme.palette.warning.main
                              : glass.accent,
                          }}
                        >
                          {initials}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight={600}>
                              {userName}
                            </Typography>
                            <Chip
                              label={member.role}
                              size="small"
                              color={getRoleColor(member.role) as any}
                              variant="outlined"
                            />
                            {isPending && (
                              <Chip
                                icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
                                label={t("billing.status_pending", "Pending")}
                                size="small"
                                color="warning"
                                variant="filled"
                                sx={{ fontSize: "0.7rem", height: 22 }}
                              />
                            )}
                            {!isPending && (
                              <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label={t("billing.status_active", "Active")}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{ fontSize: "0.7rem", height: 22 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color={glass.textSecondary}>
                            {member.user.email}
                            {member.invited_at && ` \u00B7 Invited ${new Date(member.invited_at).toLocaleDateString()}`}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={member.role}
                              onChange={(e) => {
                                if (selectedEnv) {
                                  handleUpdateMemberRole(selectedEnv.id, member.id, e.target.value);
                                }
                              }}
                              size="small"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              {ROLE_OPTIONS.map((r) => (
                                <MenuItem key={r.value} value={r.value} sx={{ fontSize: "0.85rem" }}>
                                  {t(`billing.role_${r.value}`, r.label)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Tooltip title={t("billing.remove_member", "Remove member")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                if (selectedEnv) {
                                  handleRemoveMember(selectedEnv.id, member.id);
                                }
                              }}
                            >
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={athenaDarkActionsSx}>
            <Button variant="outlined" onClick={() => setMembersModalOpen(false)} sx={athenaDarkSecondaryButtonSx}>
              {t("action.close", "Close")}
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setMembersModalOpen(false);
                if (selectedEnv) {
                  setInviteModalOpen(true);
                }
              }}
              sx={athenaDarkPrimaryButtonSx}
            >
              {t("billing.invite_member", "Invite Member")}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Plans Tab */}
      <TabPanel value={activeTab} index={1}>
        {/* Beta Tier Simulation Panel */}
        {isBetaUser && (
          <Alert
            severity="info"
            icon={<ScienceIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              backgroundColor: alpha(glass.accent, 0.15),
              border: `1px solid ${alpha(glass.accent, 0.4)}`,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BugReportIcon fontSize="small" />
                  {t("billing.beta_simulator_title", "Beta Tier Simulator")}
                </Typography>
                <Typography variant="caption" sx={{ color: glass.textPrimary }}>
                  {t("billing.beta_simulator_desc", "Experience the product as a specific tier would see it. Only affects the frontend UI.")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {simulatableTiers.map((st) => (
                  <Chip
                    key={st.name}
                    label={st.display_name}
                    size="small"
                    variant={simulatedTierName === st.name ? "filled" : "outlined"}
                    color={simulatedTierName === st.name ? "warning" : "default"}
                    onClick={() => setSimulatedTier(
                      simulatedTierName === st.name ? null : st.name
                    )}
                    sx={{
                      fontWeight: 600,
                      cursor: "pointer",
                      ...(simulatedTierName === st.name && {
                        boxShadow: `0 0 0 2px ${theme.palette.warning.main}`,
                      }),
                    }}
                  />
                ))}
                {isSimulating && (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={() => setSimulatedTier(null)}
                    sx={{ textTransform: "none", fontSize: "0.75rem", ml: 0.5 }}
                  >
                    {t("billing.stop_simulation", "Stop Simulation")}
                  </Button>
                )}
              </Box>
            </Box>
          </Alert>
        )}

        {tiersLoading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <PlanSelector
            tiers={tiers}
            currentTier={isSimulating ? simulatedTierName || undefined : organization?.tier?.name}
            onSelectTier={handleSelectTier}
            loading={checkoutLoading}
          />
        )}
        
        {/* Manage subscription via Stripe */}
        {organization?.subscription_status === "active" && organization?.tier?.name !== "free" && (
          <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CreditCardIcon />}
                onClick={handleOpenPortal}
                disabled={portalLoading}
              >
                {portalLoading ? t("billing.opening", "Opening...") : t("billing.manage_subscription", "Manage Subscription")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setCancelDialogOpen(true)}
                disabled={cancelLoading}
              >
                {cancelLoading ? t("billing.cancelling", "Cancelling...") : t("billing.cancel_subscription", "Cancel Subscription")}
              </Button>
            </Box>
            <Typography variant="caption" color={glass.textSecondary}>
              {t("billing.subscription_manage_hint", "Update payment method, change plan, or cancel your subscription")}
            </Typography>
          </Box>
        )}

        {/* Cancel confirmation dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{ backdrop: { sx: athenaBackdropSx } }}
          PaperProps={{ sx: athenaDarkPaperSx }}
        >
          <DialogTitle sx={{ ...athenaDarkTitleSx, color: theme.palette.error.light }}>
            {t("billing.cancel_subscription_title", "Cancel Subscription")}
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to cancel your <strong>{organization?.tier?.display_name}</strong> subscription?
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("billing.cancel_what_happens", "Here's what will happen:")}
            </Typography>
            <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  {t("billing.cancel_stays_active", "Your subscription stays active until the end of the current billing period.")}
                  {organization?.subscription_period_end && (
                    <> ({new Date(organization.subscription_period_end).toLocaleDateString()})</>
                  )}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  {t("billing.cancel_downgrade", "After that, your account will be moved to the Free plan.")}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="success.main" fontWeight={600}>
                  {t("billing.cancel_data_preserved", "All your environments, patients, and data will be preserved.")}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  {t("billing.cancel_resubscribe", "You can resubscribe at any time to regain access to premium features.")}
                </Typography>
              </li>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setCancelDialogOpen(false)}
              variant="outlined"
              sx={{ textTransform: "none", color: athenaColors.periwinkle }}
            >
              {t("billing.keep_subscription", "Keep My Subscription")}
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              color="error"
              disabled={cancelLoading}
            >
              {cancelLoading ? t("billing.cancelling", "Cancelling...") : t("billing.yes_cancel", "Yes, Cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Invoices Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
        }}>
            {invoicesLoading ? (
              <>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="rectangular" height={200} />
              </>
            ) : invoices.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: textSecondary }}>
                  {t("billing.no_invoices", "No invoices yet. Invoices will appear here once you have a paid subscription.")}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table sx={glassTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("billing.invoice_date", "Date")}</TableCell>
                      <TableCell>{t("billing.invoice_period", "Period")}</TableCell>
                      <TableCell>{t("billing.invoice_amount", "Amount")}</TableCell>
                      <TableCell>{t("billing.invoice_status", "Status")}</TableCell>
                      <TableCell align="right">{t("billing.invoice_actions", "Actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          {new Date(invoice.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(invoice.period_start).toLocaleDateString()} -{" "}
                          {new Date(invoice.period_end).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{invoice.amount_paid_formatted}</TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status}
                            color={invoice.status === "paid" ? "success" : "warning"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {invoice.invoice_pdf && (
                            <Tooltip title={t("billing.download_pdf", "Download PDF")}>
                              <IconButton
                                size="small"
                                href={invoice.invoice_pdf}
                                target="_blank"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {invoice.hosted_invoice_url && (
                            <Tooltip title={t("billing.view_online", "View Online")}>
                              <IconButton
                                size="small"
                                href={invoice.hosted_invoice_url}
                                target="_blank"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Box>
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={3}>
          {/* Organization Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
            }}>
                <Typography variant="h6" gutterBottom>
                  {t("billing.organization_heading", "Organization")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {/* Name */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                    {t("billing.org_name_label", "Organization Name")}
                  </Typography>
                  {editingName ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        placeholder={t("billing.organization_name", "Organization name")}
                        fullWidth
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveName}
                      >
                        {t("action.save", "Save")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setEditingName(false)}
                      >
                        {t("action.cancel", "Cancel")}
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>{organization?.name || "-"}</Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          setOrgName(organization?.name || "");
                          setEditingName(true);
                        }}
                      >
                        {t("action.edit", "Edit")}
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Subscription Status */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                    {t("billing.subscription_status_label", "Subscription Status")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography>{organization?.tier?.display_name || "—"}</Typography>
                    <Chip
                      label={organization?.subscription_status}
                      color={getStatusColor(organization?.subscription_status || "")}
                      size="small"
                    />
                  </Box>
                </Box>

                {/* Created */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                    {t("billing.member_since", "Member Since")}
                  </Typography>
                  <Typography>
                    {organization?.created_at
                      ? new Date(organization.created_at).toLocaleDateString()
                      : "-"}
                  </Typography>
                </Box>

                {/* Data Processing Region */}
                <Box>
                  <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                    {t("billing.data_region_label", "Data Processing Region")}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 220, mt: 0.5 }}>
                    <Select
                      value={organization?.data_processing_region || "eu"}
                      onChange={async (e) => {
                        const val = e.target.value as "eu" | "global";
                        try {
                          await updateSettings({ data_processing_region: val });
                        } catch { /* snackbar handled by hook */ }
                      }}
                    >
                      <MenuItem value="global">{t("billing.region_global", "Global (fastest)")}</MenuItem>
                      <MenuItem value="eu">{t("billing.region_eu", "Europe only")}</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" display="block" sx={{ color: textSecondary, mt: 0.5 }}>
                    {organization?.data_processing_region === "eu"
                      ? t("billing.region_eu_desc", "Data is processed exclusively in Sweden. Required for strict GDPR compliance.")
                      : t("billing.region_global_desc", "Data may be processed in any Azure region for best performance.")}
                  </Typography>
                </Box>

                {/* Enable AI (org-wide) */}
                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={organization?.ai_enabled || false}
                        disabled={organization?.ai_available === false}
                        onChange={async (e) => {
                          try {
                            await updateSettings({ ai_enabled: e.target.checked });
                          } catch { /* snackbar handled by hook */ }
                        }}
                      />
                    }
                    label={t("billing.enable_ai", "Enable AI")}
                    sx={{ color: textPrimary }}
                  />
                  <Typography variant="caption" display="block" sx={{ color: textSecondary }}>
                    {organization?.ai_available === false
                      ? t("billing.ai_not_in_plan", "AI is not included in your current plan.")
                      : t("billing.enable_ai_desc", "When enabled, AI extraction runs across every environment in your workspace. When disabled, documents are stored without automatic AI extraction.")}
                  </Typography>
                </Box>
            </Box>
          </Grid>

          {/* Billing Settings */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
            }}>
                <Typography variant="h6" gutterBottom>
                  {t("billing.billing_settings_heading", "Billing Settings")}
                </Typography>
                <Divider sx={{ my: 2 }} />

                {/* Overage Setting */}
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={organization?.overage_enabled || false}
                        onChange={(e) => handleOverageToggle(e.target.checked)}
                      />
                    }
                    label={t("billing.enable_overage", "Enable Overage Billing")}
                  />
                  <Typography variant="caption" display="block" sx={{ color: textSecondary }}>
                    {t("billing.overage_desc", "When enabled, usage beyond your quota will be charged to your payment method. When disabled, features will be blocked when quota is reached.")}
                  </Typography>
                </Box>

                {/* Payment Method */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                    {t("billing.payment_method_label", "Payment Method")}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CreditCardIcon />}
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    sx={{ mt: 1 }}
                  >
                    {t("billing.manage_payment", "Manage Payment Methods")}
                  </Button>
                </Box>

                {/* Cancel Subscription */}
                {organization?.subscription_status === "active" && (
                  <Box>
                    <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                      {t("billing.cancel_subscription_label", "Cancel Subscription")}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleOpenPortal}
                      sx={{ mt: 1 }}
                    >
                      {t("billing.cancel_subscription", "Cancel Subscription")}
                    </Button>
                    <Typography variant="caption" display="block" sx={{ color: textSecondary, mt: 1 }}>
                      {t("billing.cancel_desc", "You can cancel your subscription at any time. Your access will continue until the end of your billing period.")}
                    </Typography>
                  </Box>
                )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Contracts & Legal Tab */}
      <TabPanel value={activeTab} index={4}>
        <ContractsLegalTab />
      </TabPanel>

      {/* Admin Pricing Tab (superuser-only) */}
      {isSuperAdmin && (
        <TabPanel value={activeTab} index={5}>
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: "1px solid", borderColor: glass.panelBorder, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color={glass.textSecondary}>
              {t("billing.admin_panel_hint", "For monitoring, analytics, error tickets, and system config, visit the full Admin Panel.")}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => window.location.href = "/superadmin"}>
              {t("billing.open_admin_panel", "Open Admin Panel")}
            </Button>
          </Box>
          <AdminPricingPanel />
          <AdminBetaUsersPanel />
          <AdminLegalReviewPanel />
        </TabPanel>
      )}
      </VideoGlassContainer>
    </Container>
  );
};

export default BillingSettings;
